<div class="h-full flex flex-col bg-neutral-3 rounded-2xl p-4 overflow-auto font-[-apple-system,BlinkMacSystemFont,system-ui,sans-serif]">
  <div class="flex flex-wrap items-center justify-between gap-4 py-2 px-6 mb-4">
    <h2 class="text-4xl font-bold">Statistics</h2>
    <app-statistics-filters [(filters)]="filters" [locations]="locationList()" [sizes]="sizes()" />
  </div>

  @let loaded = !!data();
  @let reloading = loaded && orders.isLoading();

  <section class="bg-white rounded-2xl shadow-sm p-6 transition-opacity" [class.opacity-50]="reloading">
    <div class="flex flex-wrap items-center gap-2 mb-4 min-h-6">
      @if (loaded) {
        <p class="text-sm text-gray-400">{{ rangeLabel() }}</p>
        @if (platformLabel(); as platform) {
          <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            <img [src]="platform.icon" alt="" class="w-3 h-3" />
            {{ platform.label }}
          </span>
        }
        @if (selectedLocation(); as location) {
          <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-2 text-xs font-medium text-gray-600">
            <app-location-avatar size="sm" [location]="location" />
            {{ location.name }}
          </span>
        }
        @if (selectedSize(); as selected) {
          <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 py-0.5 pl-1 pr-2 text-xs font-medium text-gray-600">
            <app-size-glyph size="sm" [dimensions]="selected.size" [active]="true" />
            {{ selected.label }}
          </span>
        }
      } @else {
        <div class="h-4 w-48 rounded bg-gray-100 animate-pulse"></div>
      }
    </div>

    <!-- Metric tiles: the selected one drives the chart -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" role="tablist" aria-label="Metric">
      @if (loaded) {
        @for (tile of formattedTotals(); track tile.key) {
          @let active = selectedMetric() === tile.key;
          <button
            type="button"
            role="tab"
            [attr.aria-selected]="active"
            (click)="selectedMetric.set(tile.key)"
            class="rounded-xl px-4 py-3 text-left transition-colors"
            [class]="active ? 'ring-2 ring-[#0A84FF] bg-blue-50/40' : 'ring-1 ring-gray-200 hover:bg-gray-50'"
          >
            <p class="text-sm font-medium" [class]="active ? 'text-[#0A84FF]' : 'text-gray-500'">{{ tile.label }}</p>
            <p class="text-3xl font-semibold tracking-tight text-gray-900 mt-1">{{ tile.value }}</p>
          </button>
        }
      } @else {
        @for (_ of [1, 2, 3]; track $index) {
          <div class="h-[84px] rounded-xl bg-gray-100 animate-pulse"></div>
        }
      }
    </div>

    @if (loaded) {
      <div class="flex gap-8 mb-4">
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">Daily average</p>
          <p class="text-xl font-semibold text-gray-900 mt-1">{{ average() }}</p>
        </div>
        <div>
          <p class="text-xs font-medium uppercase tracking-wide text-gray-400">{{ peakLabel() }}</p>
          @if (peak(); as best) {
            <p class="text-xl font-semibold text-gray-900 mt-1">
              {{ best.value }}
              <span class="text-sm font-normal text-gray-400">{{ best.label }}</span>
            </p>
          } @else {
            <p class="text-xl font-semibold text-gray-300 mt-1">–</p>
          }
        </div>
      </div>
    }

    <div class="relative h-80">
      @if (orders.error()) {
        <div class="absolute inset-0 flex items-center justify-center">
          <p class="text-sm text-red-500">Could not load statistics. Please try again.</p>
        </div>
      } @else if (!loaded) {
        <div class="absolute inset-0 flex items-end gap-1.5">
          @for (height of [40, 65, 30, 80, 55, 70, 45, 90, 35, 60, 50, 75]; track $index) {
            <div class="flex-1 rounded-md bg-gray-100 animate-pulse" [style.height.%]="height"></div>
          }
        </div>
      } @else {
        <canvas baseChart type="bar" [data]="chartData()" [options]="chartOptions()"></canvas>
        @if (totals().count === 0) {
          <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p class="text-sm text-gray-400">No printed orders for the selected filters</p>
          </div>
        }
      }
    </div>
  </section>
</div>
